import React, { useState, useMemo } from 'react';
import { ChevronDown, Plus, Trash2, Clock, User, Scissors, Sparkles, Calculator, Check } from 'lucide-react';

const emptyOutfit = () => ({
  mainOutfit: '', mainColor: '', mainVendor: '', mainPrice: '', mainActual: '',
  secondLook: '', secondVendor: '', secondPrice: '',
  shoes: '', shoesVendor: '', shoesPrice: '',
  jewelry: '', underwear: '',
  veilCapeJacket: '', accessories: '',
  sizes: '', fittings: '', alterations: '',
  totalEstimated: '', totalActual: '', paidAmount: '', notes: '',
});

const emptyBeauty = () => ({
  hair: '', hairVendor: '', hairPrice: '', hairTrial: '',
  makeup: '', makeupVendor: '', makeupPrice: '', makeupTrial: '',
  nails: '', nailsVendor: '', nailsPrice: '',
  brows: '', browVendor: '',
  skincare: '', sprayTan: '', grooming: '', treatments: '',
  notes: '',
});

const emptyPartyMember = () => ({
  id: `pm-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
  name: '', role: '', outfit: '', sizes: '', beautyTime: '', notes: '',
});

const emptyScheduleItem = () => ({
  id: `sc-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
  time: '', task: '', who: '', notes: '',
});

const fmt = (val) => { const n = parseFloat(val); return isNaN(n) ? '' : n.toLocaleString('en-US'); };

const F = ({ label, value, onChange, placeholder, type, half }) => (
  <div className={half ? '' : 'col-span-2'}>
    <label className="text-[9px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <input type={type || 'text'} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''}
      className="w-full text-[11px] font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
  </div>
);

export const AttirePlanner = ({ attire, onChange, couple }) => {
  const [activeTab, setActiveTab] = useState('person1');
  const [openSections, setOpenSections] = useState(['outfit']);
  const data = attire || {};

  const p1Name = couple?.person1Name || 'Person 1';
  const p2Name = couple?.person2Name || 'Person 2';

  const toggle = (id) => setOpenSections(p => p.includes(id) ? p.filter(s => s !== id) : [...p, id]);

  const updatePerson = (person, section, updates) => {
    const pd = data[person] || {};
    onChange({ ...data, [person]: { ...pd, [section]: { ...(pd[section] || {}), ...updates } } });
  };
  const updateParty = (newParty) => onChange({ ...data, party: newParty });
  const updateSchedule = (key, newSchedule) => onChange({ ...data, [key]: newSchedule });

  const party = data.party || [];
  const prewedSchedule = data.prewedSchedule || [];
  const daySchedule = data.daySchedule || [];

  const tabs = [
    { id: 'person1', label: p1Name },
    { id: 'person2', label: p2Name },
    { id: 'party', label: 'Wedding party' },
    { id: 'schedule', label: 'Tidslinjer' },
  ];

  const totals = useMemo(() => {
    let est = 0, act = 0;
    ['person1', 'person2'].forEach(p => {
      const o = data[p]?.outfit;
      if (o) {
        [o.mainPrice, o.secondPrice, o.shoesPrice].forEach(v => { const n = parseFloat(v); if (!isNaN(n)) est += n; });
        const a = parseFloat(o.totalActual); if (!isNaN(a)) act += a;
      }
      const b = data[p]?.beauty;
      if (b) {
        [b.hairPrice, b.makeupPrice, b.nailsPrice].forEach(v => { const n = parseFloat(v); if (!isNaN(n)) est += n; });
      }
    });
    return { est, act };
  }, [data]);

  const renderPersonTab = (person) => {
    const pd = data[person] || {};
    const outfit = pd.outfit || emptyOutfit();
    const beauty = pd.beauty || emptyBeauty();
    const uO = (updates) => updatePerson(person, 'outfit', updates);
    const uB = (updates) => updatePerson(person, 'beauty', updates);

    return (
      <div className="space-y-2">
        {/* Outfit section */}
        <Section title="Attire" id="outfit" isOpen={openSections.includes('outfit')} toggle={toggle} filled={!!outfit.mainOutfit?.trim()}>
          <div className="grid grid-cols-2 gap-2">
            <F label="Hovedantrekk" value={outfit.mainOutfit} onChange={v => uO({ mainOutfit: v })} placeholder="Description" half />
            <F label="Farge" value={outfit.mainColor} onChange={v => uO({ mainColor: v })} placeholder="e.g. elfenben" half />
            <F label="Vendor" value={outfit.mainVendor} onChange={v => uO({ mainVendor: v })} placeholder="Butikk/designer" half />
            <F label="Price (kr)" value={outfit.mainPrice} onChange={v => uO({ mainPrice: v })} type="number" half />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <F label="Second Look / Backup" value={outfit.secondLook} onChange={v => uO({ secondLook: v })} half />
            <F label="Second Look vendor" value={outfit.secondVendor} onChange={v => uO({ secondVendor: v })} half />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <F label="Shoes" value={outfit.shoes} onChange={v => uO({ shoes: v })} placeholder="Merke/stil" half />
            <F label="Shoes price (kr)" value={outfit.shoesPrice} onChange={v => uO({ shoesPrice: v })} type="number" half />
            <F label="Jewelry" value={outfit.jewelry} onChange={v => uO({ jewelry: v })} half />
            <F label="Underwear" value={outfit.underwear} onChange={v => uO({ underwear: v })} half />
            <F label="Veil / Cape / Jacket / Bow tie" value={outfit.veilCapeJacket} onChange={v => uO({ veilCapeJacket: v })} half />
            <F label="Other accessories" value={outfit.accessories} onChange={v => uO({ accessories: v })} half />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <F label="Sizer" value={outfit.sizes} onChange={v => uO({ sizes: v })} placeholder="Kjole 38, sko 39..." half />
            <F label="Tryetimer / Fittings" value={outfit.fittings} onChange={v => uO({ fittings: v })} placeholder="Dateer, antall" half />
            <F label="Alterationser" value={outfit.alterations} onChange={v => uO({ alterations: v })} half />
            <F label="Paid amount (kr)" value={outfit.paidAmount} onChange={v => uO({ paidAmount: v })} type="number" half />
          </div>
          <F label="Notes" value={outfit.notes} onChange={v => uO({ notes: v })} placeholder="Ekstra info..." />
        </Section>

        {/* Beauty section */}
        <Section title="Beauty" id="beauty" isOpen={openSections.includes('beauty')} toggle={toggle} filled={!!beauty.hair?.trim()}>
          <div className="grid grid-cols-2 gap-2">
            <F label="Hair" value={beauty.hair} onChange={v => uB({ hair: v })} placeholder="Style/description" half />
            <F label="Hair vendor" value={beauty.hairVendor} onChange={v => uB({ hairVendor: v })} half />
            <F label="Hair price (kr)" value={beauty.hairPrice} onChange={v => uB({ hairPrice: v })} type="number" half />
            <F label="Hair trial" value={beauty.hairTrial} onChange={v => uB({ hairTrial: v })} placeholder="Date" half />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <F label="Makeup" value={beauty.makeup} onChange={v => uB({ makeup: v })} half />
            <F label="Makeup vendor" value={beauty.makeupVendor} onChange={v => uB({ makeupVendor: v })} half />
            <F label="Makeup price (kr)" value={beauty.makeupPrice} onChange={v => uB({ makeupPrice: v })} type="number" half />
            <F label="Makeup trial" value={beauty.makeupTrial} onChange={v => uB({ makeupTrial: v })} placeholder="Date" half />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <F label="Negler" value={beauty.nails} onChange={v => uB({ nails: v })} half />
            <F label="Nails vendor / price" value={beauty.nailsVendor} onChange={v => uB({ nailsVendor: v })} half />
            <F label="Bryn" value={beauty.brows} onChange={v => uB({ brows: v })} half />
            <F label="Brow vendor" value={beauty.browVendor} onChange={v => uB({ browVendor: v })} half />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <F label="Hudpleie" value={beauty.skincare} onChange={v => uB({ skincare: v })} half />
            <F label="Spray tan" value={beauty.sprayTan} onChange={v => uB({ sprayTan: v })} half />
            <F label="Barbering / Grooming" value={beauty.grooming} onChange={v => uB({ grooming: v })} half />
            <F label="Andre behandlinger" value={beauty.treatments} onChange={v => uB({ treatments: v })} half />
          </div>
          <F label="Notes" value={beauty.notes} onChange={v => uB({ notes: v })} />
        </Section>
      </div>
    );
  };

  return (
    <div data-testid="attire-planner" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Attire & Beauty</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Plan attire, accessories and beauty timelines</p>
        </div>
      </div>

      {(totals.est > 0 || totals.act > 0) && (
        <div className="bg-white rounded-xl border border-stone-200/80 p-3 flex items-center gap-4" data-testid="attire-budget">
          <Calculator className="w-3.5 h-3.5 text-stone-400" />
          <div className="flex items-center gap-4 text-[10px] font-sans">
            <div><span className="text-stone-400">Estimert: </span><span className="font-bold text-stone-700">{fmt(totals.est)} kr</span></div>
            {totals.act > 0 && <div><span className="text-stone-400">Faktisk: </span><span className="font-bold text-stone-700">{fmt(totals.act)} kr</span></div>}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-stone-100 rounded-xl p-1" data-testid="attire-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`attire-tab-${t.id}`}
            className={`flex-1 text-[10px] font-sans font-semibold py-2 rounded-lg transition-all ${
              activeTab === t.id ? 'bg-white text-primary shadow-sm' : 'text-stone-500 hover:text-stone-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      <div className="animate-fade-in-up" data-testid="attire-content">
        {(activeTab === 'person1' || activeTab === 'person2') && renderPersonTab(activeTab)}

        {activeTab === 'party' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-sans font-bold text-stone-600">Wedding party ({party.length})</p>
              <button onClick={() => updateParty([...party, emptyPartyMember()])}
                className="flex items-center gap-1 text-[10px] font-sans font-semibold text-primary hover:text-primary/80" data-testid="add-party-member">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {party.length === 0 && (
              <p className="text-[10px] font-sans text-stone-400 text-center py-6">No one in the wedding party yet</p>
            )}
            {party.map((m, i) => (
              <div key={m.id} className="bg-white rounded-xl border border-stone-200/80 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-3 h-3 text-stone-400" />
                  <input value={m.name} onChange={e => { const np = [...party]; np[i] = { ...m, name: e.target.value }; updateParty(np); }}
                    placeholder="Name" className="flex-1 text-[11px] font-sans font-medium px-2 py-1 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                  <input value={m.role} onChange={e => { const np = [...party]; np[i] = { ...m, role: e.target.value }; updateParty(np); }}
                    placeholder="Role (e.g. forlover)" className="w-36 text-[11px] font-sans px-2 py-1 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                  <button onClick={() => updateParty(party.filter((_, j) => j !== i))} className="p-1 text-stone-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input value={m.outfit} onChange={e => { const np = [...party]; np[i] = { ...m, outfit: e.target.value }; updateParty(np); }}
                    placeholder="Attire" className="text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                  <input value={m.sizes} onChange={e => { const np = [...party]; np[i] = { ...m, sizes: e.target.value }; updateParty(np); }}
                    placeholder="Sizer" className="text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                  <input value={m.beautyTime} onChange={e => { const np = [...party]; np[i] = { ...m, beautyTime: e.target.value }; updateParty(np); }}
                    placeholder="Beauty-tid" className="text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="space-y-4">
            {/* Pre-wedding timeline */}
            <ScheduleSection title="Preparations (uker/dager f\u00f8r)" items={prewedSchedule}
              onUpdate={items => updateSchedule('prewedSchedule', items)} timePlaceholder="e.g. 3 mnd f\u00f8r" />
            {/* Day-of beauty schedule */}
            <ScheduleSection title="Bryllupsdagen (time for time)" items={daySchedule}
              onUpdate={items => updateSchedule('daySchedule', items)} timePlaceholder="e.g. 08:00" />
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, id, isOpen, toggle, filled, children }) => (
  <div className={`rounded-xl border ${filled ? 'border-primary/20 bg-primary/[0.02]' : 'border-stone-200/80 bg-white'}`}>
    <button onClick={() => toggle(id)} className="flex items-center w-full px-4 py-2.5 text-left gap-2">
      <Scissors className="w-3.5 h-3.5 text-stone-400" />
      <span className="flex-1 text-[11px] font-sans font-semibold text-stone-700">{title}</span>
      {filled && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
      <ChevronDown className={`w-3 h-3 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && <div className="px-4 pb-3 border-t border-stone-100 pt-2.5">{children}</div>}
  </div>
);

const ScheduleSection = ({ title, items, onUpdate, timePlaceholder }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <p className="text-[10px] font-sans font-bold text-stone-600 flex items-center gap-1"><Clock className="w-3 h-3" /> {title}</p>
      <button onClick={() => onUpdate([...items, emptyScheduleItem()])}
        className="flex items-center gap-1 text-[10px] font-sans font-semibold text-primary hover:text-primary/80">
        <Plus className="w-3 h-3" /> Add
      </button>
    </div>
    {items.length === 0 && <p className="text-[10px] font-sans text-stone-400 text-center py-4">None oppf\u00f8ringer enn\u00e5</p>}
    <div className="space-y-1.5">
      {items.map((item, i) => (
        <div key={item.id} className="flex items-center gap-2 bg-white rounded-lg border border-stone-200 px-2.5 py-1.5">
          <input value={item.time} onChange={e => { const n = [...items]; n[i] = { ...item, time: e.target.value }; onUpdate(n); }}
            placeholder={timePlaceholder} className="w-24 text-[10px] font-sans font-bold px-1.5 py-0.5 border border-stone-200 rounded focus:border-primary focus:outline-none" />
          <input value={item.task} onChange={e => { const n = [...items]; n[i] = { ...item, task: e.target.value }; onUpdate(n); }}
            placeholder="Task" className="flex-1 text-[10px] font-sans px-1.5 py-0.5 border border-stone-200 rounded focus:border-primary focus:outline-none" />
          <input value={item.who} onChange={e => { const n = [...items]; n[i] = { ...item, who: e.target.value }; onUpdate(n); }}
            placeholder="Hvem" className="w-24 text-[10px] font-sans px-1.5 py-0.5 border border-stone-200 rounded focus:border-primary focus:outline-none" />
          <button onClick={() => onUpdate(items.filter((_, j) => j !== i))} className="p-0.5 text-stone-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
        </div>
      ))}
    </div>
  </div>
);
